import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  Chip,
} from "@mui/material";

const PublicQRView = () => {
  const { packageId } = useParams();
  const location = useLocation();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPackageDetails();
  }, [packageId]);

  const fetchPackageDetails = async () => {
    try {
      const uuid = location.pathname === '/hindalco' 
        ? new URLSearchParams(location.search).get('uuid')
        : packageId;

      if (!uuid) {
        throw new Error('No package ID provided');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/get?uuid=${uuid}`);

      if (!response.ok) {
        throw new Error('Package not found');
      }

      const data = await response.json();
      setPackageData(data);
    } catch (error) {
      console.error('Error fetching package details:', error);
      setError('Package not found or unable to load details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress size={60} sx={{ color: '#1F425C' }} />
      </Box>
    );
  }

  if (error || !packageData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3
      }}>
        <img 
          src="/HINDALCO.NS_BIG.png" 
          alt="Hindalco" 
          style={{ 
            height: '80px', 
            width: 'auto',
            marginBottom: '24px'
          }} 
        />
        <Typography variant="h5" color="error" sx={{ mb: 2, textAlign: 'center' }}>
          Package Not Found
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center' }}>
          The package you're looking for could not be found or may have been removed.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      py: 4
    }}>
      {/* Header with Hindalco Logo */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 4,
        bgcolor: '#1F425C',
        py: 3,
        color: 'white'
      }}>
        <img 
          src="/HINDALCO.NS_BIG.png" 
          alt="Hindalco" 
          style={{ 
            height: '60px', 
            width: 'auto',
            marginBottom: '16px'
          }} 
        />
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Package Information
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Hindalco Industries Limited
        </Typography>
      </Box>

      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Package Details Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ color: '#1F425C', fontWeight: 600, mb: 2 }}>
              Package Details
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Here are the details for your package
            </Typography>
            <Divider sx={{ mt: 3, mb: 2 }} />
          </Box>

          {/* Package Information Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Package Number
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {packageData?.package_number}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Vendor Name
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {packageData?.vendor_name}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Location
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {packageData?.location || 'Not specified'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Owner
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {packageData?.owner || 'Not specified'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Delivery Date
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {new Date(packageData?.delivery_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long', 
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Created By
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {packageData?.qr_created_by}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 1 }}>
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  {new Date(packageData?.qr_created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Items Section */}
          <Box>
            <Typography variant="h6" sx={{ color: '#1F425C', fontWeight: 600, mb: 3 }}>
              Items ({packageData?.items?.length || 0})
            </Typography>
            
            {packageData?.items && packageData.items.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {packageData.items.map((item, index) => (
                  <Chip
                    key={index}
                    label={`${index + 1}. ${item}`}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      bgcolor: '#E8F4FD',
                      borderColor: '#93c5fd',
                      color: '#1F425C',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: '#bfdbfe'
                      }
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                No items listed for this package
              </Typography>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ 
            mt: 4, 
            pt: 3, 
            borderTop: '1px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
              For any questions or concerns, please contact Hindalco customer service
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicQRView; 